import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, Camera, Paperclip, X, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import UserSelect from "@/components/UserSelect";
import PhotoUploader from "@/components/PhotoUploader";

const STATUS = [
  { value: "open", label: "Abierta", cls: "bg-red-100 text-red-800 border-red-200" },
  { value: "in_progress", label: "En progreso", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "verified", label: "Verificada", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "closed", label: "Cerrada", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];
const PRIORITY = [
  { value: "low", label: "Baja", cls: "bg-slate-100 text-slate-700" },
  { value: "medium", label: "Media", cls: "bg-amber-100 text-amber-800" },
  { value: "high", label: "Alta", cls: "bg-orange-100 text-orange-800" },
  { value: "critical", label: "Crítica", cls: "bg-red-100 text-red-800" },
];
const SOURCE = [
  { value: "observation", label: "Observación (OCAS)" },
  { value: "incident", label: "Incidente" },
  { value: "inspection", label: "Inspección" },
  { value: "audit", label: "Auditoría" },
  { value: "investigation", label: "Investigación" },
];

const emptyForm = () => ({ source_type: "observation", source_id: "", description: "", responsible: "", due_date: "", priority: "medium", status: "open", evidence: "", project_id: defaultProject() });

/** Dialog de cierre/verificación — exige foto O nota ≥10 chars. */
function CloseWithEvidenceDialog({ action, targetStatus, onDone, onCancel }) {
  const [photos, setPhotos] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const needsEvidence = targetStatus === "verified" || targetStatus === "closed";
  const canSave = photos.length > 0 || note.trim().length >= 10;

  const submit = async () => {
    if (!canSave) {
      toast.error("Sube al menos una foto O escribe una nota de mínimo 10 caracteres");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/hse/actions/${action.id}/status`, {
        status: targetStatus,
        evidence_photos: photos,
        evidence_note: note.trim(),
      });
      toast.success(targetStatus === "closed"
        ? "Acción cerrada — OCAS vinculada cerrada también"
        : "Acción verificada con evidencia");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`action-close-dialog-${action.id}`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CheckCircle weight="bold" className="text-emerald-600" />
            {targetStatus === "closed" ? "Cerrar acción con evidencia" : "Verificar acción con evidencia"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-sm">
            <div className="text-slate-500 text-[11px] uppercase tracking-wider">Acción</div>
            <div className="font-semibold text-slate-800">{action.description}</div>
            <div className="text-xs text-slate-500 mt-1">Responsable: {action.responsible} · Origen: {SOURCE.find((s) => s.value === action.source_type)?.label}</div>
          </div>

          {needsEvidence && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              📸 <b>Se requiere evidencia:</b> sube fotos (recomendado) o describe la corrección aplicada (mínimo 10 caracteres). Si la acción viene de una OCAS, la evidencia se copia automáticamente al cerrar la observación.
            </div>
          )}

          <div>
            <Label className="overline-label flex items-center gap-1"><Camera size={12} /> Fotos de evidencia (máx 6)</Label>
            <PhotoUploader
              value={photos}
              onChange={setPhotos}
              max={6}
              testidPrefix={`action-evidence-${action.id}`}
            />
          </div>

          <div>
            <Label className="overline-label flex items-center gap-1"><Paperclip size={12} /> Nota de corrección {note.trim().length > 0 && `(${note.trim().length} chars)`}</Label>
            <Textarea
              rows={4}
              data-testid={`action-evidence-note-${action.id}`}
              placeholder="Describe qué se hizo para corregir. Ej: 'Se instaló nueva línea de vida, se capacitó al operador y se validó con supervisor.'"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-3">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancelar</Button>
          <Button
            data-testid={`action-close-save-${action.id}`}
            onClick={submit}
            disabled={saving || !canSave}
            className="bg-emerald-700 hover:bg-emerald-800 gap-1"
          >
            <CheckCircle size={14} weight="bold" /> {saving ? "Guardando…" : (targetStatus === "closed" ? "Cerrar" : "Verificar")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Dialog lightbox — muestra fotos + nota de evidencia guardada. */
function EvidenceViewer({ action, onClose }) {
  const photos = action.evidence_photos || [];
  const note = action.evidence_note || action.evidence || "";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={`action-evidence-viewer-${action.id}`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MagnifyingGlassPlus weight="bold" /> Evidencia de cierre
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-2">
          {action.closed_by && <span>Cerrada por <b>{action.closed_by}</b></span>}
          {action.completed_at && <span> · {new Date(action.completed_at).toLocaleString("es-MX")}</span>}
        </div>
        {photos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <a key={i} href={p} target="_blank" rel="noreferrer" className="block border border-slate-200 rounded overflow-hidden hover:border-blue-500">
                <img src={p} alt={`evidencia ${i + 1}`} className="w-full h-32 object-cover" />
              </a>
            ))}
          </div>
        )}
        {note && (
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-800 whitespace-pre-wrap mt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Nota</div>
            {note}
          </div>
        )}
        {photos.length === 0 && !note && (
          <p className="text-sm text-slate-400 italic">Esta acción no tiene evidencia registrada aún.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ActionsModule() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [closeState, setCloseState] = useState(null);     // {action, targetStatus} | null
  const [viewer, setViewer] = useState(null);             // action | null
  const canWrite = ["admin", "manager", "supervisor"].includes(user?.role);
  const [filter, setFilter] = useState("all");
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => { api.get("/hse/actions").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!assertProject(form.project_id)) return;
    try { await api.post("/hse/actions", form); toast.success("Acción creada"); setOpen(false); setForm(emptyForm()); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const handleStatusChange = async (action, newStatus) => {
    // Cerrar/verificar requiere evidencia → abre dialog. Otros estados directo.
    if (newStatus === "verified" || newStatus === "closed") {
      setCloseState({ action, targetStatus: newStatus });
      return;
    }
    try { await api.post(`/hse/actions/${action.id}/status`, { status: newStatus }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await api.delete(`/hse/actions/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = filter === "all" ? items : filter === "overdue"
    ? items.filter((a) => (a.status === "open" || a.status === "in_progress") && a.due_date && a.due_date < today)
    : items.filter((a) => a.status === filter);

  const hasEvidence = (it) => (it.evidence_photos && it.evidence_photos.length > 0) || (it.evidence_note || "").length > 0 || (it.evidence || "").length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1">
          {[{ v: "all", l: "Todas" }, ...STATUS.map((s) => ({ v: s.value, l: s.label })), { v: "overdue", l: "⚠ Vencidas" }].map((f) => (
            <button key={f.v} data-testid={`actions-filter-${f.v}`} onClick={() => setFilter(f.v)} className={`text-xs px-3 py-1 rounded-full border transition-colors ${filter === f.v ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{f.l}</button>
          ))}
        </div>
        {canWrite && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyForm()); }}>
            <DialogTrigger asChild><Button data-testid="action-new-button" className="bg-blue-900 hover:bg-blue-800 gap-2"><Plus size={14} weight="bold" /> Nueva acción</Button></DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Nueva acción correctiva</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <ProjectField value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="action-project" />
                <div><Label className="overline-label">Descripción</Label>
                  <Textarea rows={3} data-testid="action-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="overline-label">Responsable</Label>
                    <UserSelect testid="action-responsible" value={form.responsible} onChange={(v) => setForm({ ...form, responsible: v })} /></div>
                  <div><Label className="overline-label">Fecha límite</Label>
                    <DateField testid="action-due-date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="overline-label">Prioridad</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="action-priority"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{PRIORITY.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="overline-label">Origen</Label>
                    <Select value={form.source_type} onValueChange={(v) => setForm({ ...form, source_type: v })}>
                      <SelectTrigger data-testid="action-source"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{SOURCE.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
              </div>
              <Button data-testid="action-save" onClick={save} disabled={!form.description || !form.responsible || !form.due_date} className="bg-blue-900 hover:bg-blue-800 w-full">Crear</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr><th>Acción</th><th>Responsable</th><th>Origen</th><th>Prioridad</th><th>Fecha límite</th><th>Estado</th><th className="text-center">Evidencia</th>{canWrite && <th></th>}</tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={canWrite ? 8 : 7} className="text-center text-slate-400 py-8">Sin acciones</td></tr>}
            {filtered.map((it) => {
              const overdue = (it.status === "open" || it.status === "in_progress") && it.due_date && it.due_date < today;
              const pr = PRIORITY.find((p) => p.value === it.priority) || PRIORITY[1];
              const withEvidence = hasEvidence(it);
              return (
                <tr key={it.id} data-testid={`action-row-${it.id}`}>
                  <td className="text-sm"><ProjectBadge id={it.project_id} className="mr-1 align-middle" />{it.description}</td>
                  <td className="text-xs text-slate-500">{it.responsible}</td>
                  <td className="text-xs">{SOURCE.find((s) => s.value === it.source_type)?.label || it.source_type}</td>
                  <td><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${pr.cls}`}>{pr.label}</span></td>
                  <td className={`font-mono text-xs ${overdue ? "text-red-600 font-bold" : ""}`}>{it.due_date}{overdue && " ⚠"}</td>
                  <td>
                    {canWrite ? (
                      <Select value={it.status} onValueChange={(v) => handleStatusChange(it, v)}>
                        <SelectTrigger data-testid={`action-status-${it.id}`} className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${STATUS.find((s) => s.value === it.status)?.cls || ""}`}>{STATUS.find((s) => s.value === it.status)?.label}</span>
                    )}
                  </td>
                  <td className="text-center">
                    {withEvidence ? (
                      <button
                        data-testid={`action-evidence-view-${it.id}`}
                        onClick={() => setViewer(it)}
                        className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 text-xs font-semibold"
                        title="Ver evidencia"
                      >
                        <Camera size={14} weight="fill" />
                        {(it.evidence_photos?.length || 0) > 0 && <span>({it.evidence_photos.length})</span>}
                      </button>
                    ) : (
                      <span className="text-slate-300 text-xs" title="Sin evidencia"><X size={12} /></span>
                    )}
                  </td>
                  {canWrite && <td><button data-testid={`action-delete-${it.id}`} onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {closeState && (
        <CloseWithEvidenceDialog
          action={closeState.action}
          targetStatus={closeState.targetStatus}
          onDone={() => { setCloseState(null); load(); }}
          onCancel={() => setCloseState(null)}
        />
      )}
      {viewer && <EvidenceViewer action={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
